import {
  InboxOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Tag,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import clsx from 'clsx';
import { debounce, groupBy, includes, isEmpty, map } from 'lodash';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import {
  ArchiveOutline,
  DeleteButton,
  EditIcon,
  Import,
  PersonDone,
  SaveOutline,
  WrongIcon,
} from '../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  CONFIRMATION_TYPES,
  DATE_ISO_FORMAT,
  DATETIMEWITHDIVIDE,
  DEFAULTDATEFORMAT,
  DEFAULTDATETIMEFORMAT,
  GA_EVENT,
  GA_LABEL,
  PROJECT_ROLES_KEYS,
  PROPERTY_TYPES,
  REGISTER_LOG_STATUS,
  REGISTER_LOG_STATUS_CLASSNAME,
  REGISTER_STATUS_LABEL,
  ROUTES,
  SCROLL_PERCENT,
  SESSION_STORAGE_KEYS,
  STAGE_STATUS_CLASSNAME,
  TAB_KEYS,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
  USER_ROLES,
} from '../../../../../../../common/constants';
import { getValidToken } from '../../../../../../../common/tokenManager';
import { Event } from '../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../common/useRouter';
import {
  calculateFieldValue,
  formatNumericInput,
  formValidatorRules,
  titleCase,
  toCamelCase,
  transformAggregatesForExportURL,
} from '../../../../../../../common/utils';
import CanPerform from '../../../../../../../components/CanPerform';
import CommonCard from '../../../../../../../components/CommonCard';
import CommonPopover from '../../../../../../../components/CommonPopover';
import CommonSelect from '../../../../../../../components/CommonSelect';
import EllipsisText from '../../../../../../../components/EllipsisText';
import Portal from '../../../../../../../components/Portal';
import {
  SAVE_REGISTER_LOG_TO_DRAFT,
  UPDATE_REGISTER,
  UPDATE_REGISTER_LOG_DETAIL_ENTRY_LIST,
} from '../../../../../graphql/Mutation';
import {
  GET_AFFECTED_LOGS_COUNT,
  GET_REGISTER,
  GET_REGISTER_LOG_DETAILS_ENTRY,
  REGISTER_LOGS_EXIST_BY_REGISTER,
} from '../../../../../graphql/Queries';
import ApproversDrawer from '../../eqcTypes/ApproversDrawer';
import ConfirmationModal from '../ConfirmationModal';
import DeleteRegisterModal from '../DeleteRegisterModal';
import RegisterCommentModal from '../RegisterCommentModal';
import RegisterFieldsTable from '../RegisterFieldsTable';
import RenderFields from '../RenderFields';
import RegisterApprovalModal from './RegisterApprovalModal';
import RegisterDetailsTable from './RegisterDetailsTable';
import SaveRegisterLogModal from './SaveRegisterLogModal';
import SeekApprovalModal from './SeekApprovalModal';
import UnsavedChangesPrompt from './UnsavedChangesPrompt';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const getStorageKey = (id) =>
  `${SESSION_STORAGE_KEYS?.REGISTER_AGGREGATES_ENTRIES_KEY}-${id}`;

const RegisterPropertyDetails = ({
  registerDetailsData,
  registerId: propsRegisterId,
  historyOptions,
  selectedHistoryId,
  historyData,
  historyLoading,
  registerLog,
  handleHistorySelect,
  handleHisotryRefetch,
}) => {
  const {
    state,
    getCurrentUserRole,
    getCurrentUser,
    dispatch,
    getCurrentTenant,
    getTenantUser,
    getCurrentProjectDetail,
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const {
    location,
    navigate,
    params: { registerId: mainRegisterId, projectId, registerLogId },
  } = useRouter();

  // Use propsRegisterId from parent if available, otherwise use route registerId
  const registerId = location?.pathname?.includes('/edit-control')
    ? mainRegisterId
    : propsRegisterId;

  const initialFilter = useMemo(
    () => ({
      limit: 30,
      skip: 0,
      registerLogHistoryId: historyData?.isLatest ? undefined : historyData?.id,
      isFromTodo: false,
    }),
    [historyData?.id],
  );
  const registerName = useWatch('registerName', form);
  const [registerData, setRegisterData] = useState();
  const [showAll, setShowAll] = useState(false);
  const [showDeletedEntries, setShowDeletedEntries] = useState(false);
  const [isEditableProperty, setIsEditableProperty] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const properties = [];
  const [propertyDetails, setPropertyDetails] = useState([]);
  const [totalDetailsCount, setTotalDetailsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditControl, setIsEditControl] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [registerHasLogs, setRegisterHasLogs] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteRegisterModal, setShowDeleteRegisterModal] = useState(false);
  const [confirmationType, setConfirmationType] = useState(
    CONFIRMATION_TYPES?.EDIT,
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isOptionsFocused, setIsOptionsFocused] = useState(null);
  const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
    useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [isDateFocused, setIsDateFocused] = useState(null);
  const [isTimeFocused, setIsTimeFocused] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [affectedLogsCount, setAffectedLogsCount] = useState(0);
  const [showSeekApprovalModal, setShowSeekApprovalModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawerData, setDrawerData] = useState();
  const [showDetailsCommentModal, setShowDetailsCommentModal] = useState(false);
  const [selectedDetailsComment, setSelectedDetailsComment] = useState(null);

  const [selectedAggregates, setSelectedAggregates] = useState({});
  const showDetailPrompt = useRef(false);
  const showTableFieldsPrompt = useRef(false);
  const refetchRef = useRef();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isProjectAdminOrInspector = includes(
    [PROJECT_ROLES_KEYS.PROJECT_ADMIN, PROJECT_ROLES_KEYS.INSPECTOR],
    state?.projectRole,
  );
  const isAuditor =
    getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
    state?.projectRole === PROJECT_ROLES_KEYS.AUDITOR;
  const currentUser = getCurrentUser();
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const fieldsData = useRef([]);
  const tableListRef = useRef(null);
  // Track detail entry IDs that the user has actually modified in the current session
  const modifiedDetailIds = useRef(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [detailsFormHasErrors, setDetailsFormHasErrors] = useState(false);
  const [tableFormHasErrors, setTableFormHasErrors] = useState(false);
  const [isLogDraft, setIsLogDraft] = useState('');
  const eqcIdToLabelMap = useRef({});
  const projectDetails = getCurrentProjectDetail();
  const nomenclatureStatus = projectDetails?.isNomenclaturePublished;
  const [showTableFieldConfirmModal, setShowTableFieldConfirmModal] =
    useState(false);

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  // Helper function to check if a field was updated after history entry creation
  const isFieldUpdatedAfterHistory = useCallback(
    (fieldUpdatedAt, registerDetailId) => {
      if (!historyData?.createdAt || !fieldUpdatedAt) return false;
      if (!historyData?.isLatest) return false;
      if (
        !includes(
          [REGISTER_LOG_STATUS?.REDO, REGISTER_LOG_STATUS?.REDO_DRAFT],
          registerLog?.status,
        )
      )
        return false;

      // Check if this entry was modified by the user in the current session
      const wasModifiedInSession = modifiedDetailIds?.current?.has(
        registerDetailId,
      );

      // If the entry was modified in the current session, show the blue dot
      if (wasModifiedInSession) {
        return true;
      }

      // For entries not modified in this session, use timestamp comparison
      // with a buffer to filter out system updates during REDO processing
      const historyCreatedAtMoment = moment(historyData?.createdAt);
      const fieldUpdatedAtMoment = moment(fieldUpdatedAt);

      // Only show blue dot if field was updated more than 30 seconds after REDO creation
      // This filters out system updates that happen during REDO processing
      const diffInSeconds = fieldUpdatedAtMoment?.diff(
        historyCreatedAtMoment,
        'seconds',
      );

      return diffInSeconds > 30;
    },
    [historyData, registerLog?.status],
  );

  useEffect(() => {
    if (
      includes([REGISTER_STATUS_LABEL.LIVE], registerDetailsData?.status) &&
      !location?.pathname?.includes('/edit-control')
    ) {
      setIsEditable(true);
    }
  }, [registerDetailsData]);

  const checkDetailsFormErrors = () => {
    const errors = form?.getFieldsError();
    const hasErrors = errors?.some((field) => field?.errors?.length > 0);
    setDetailsFormHasErrors(hasErrors);
  };

  const [getRegister] = useLazyQueryWithCallbacks(GET_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(response) {
      if (
        includes(
          [REGISTER_STATUS_LABEL.DRAFT, REGISTER_STATUS_LABEL.UNPUBLISHED],
          response?.getRegister?.status,
        )
      ) {
        setIsEditable(true);
      }
      setRegisterData(response?.getRegister);
      form.setFieldValue('registerName', response?.getRegister?.name);
      const registerFormFields = response?.getRegister?.registerFormFields;
      // Filter out deleted fields
      const activeFormFields = registerFormFields?.filter(
        (field) => !field?.deletedAt,
      );
      const registerDetails = response?.getRegister?.registerDetails;
      const activeRegisterDetails = registerDetails?.filter(
        (field) => !field?.deletedAt,
      );
      setPropertyDetails(activeRegisterDetails);
      setTableColumns(activeFormFields);
    },
  });

  const [getRegisterLogDetails, { loading }] = useLazyQueryWithCallbacks(
    GET_REGISTER_LOG_DETAILS_ENTRY,
    {
      fetchPolicy: 'network-only',
      onCompleted(res) {
        const data = res?.registerLogDetailEntryList?.data;
        const isDraft =
          res?.registerLogDetailEntryList?.registerLogStatus ===
          REGISTER_LOG_STATUS?.DRAFT;
        setIsLogDraft(isDraft);
        setTotalDetailsCount(res?.registerLogDetailEntryList?.total);
        const currentFormValues = form?.getFieldsValue();
        const processValue = (item, value) => {
          if (
            item?.type === PROPERTY_TYPES?.EQC &&
            typeof value === 'string' &&
            value
          ) {
            return value?.split(',');
          }
          const isDefaultDateFormat =
            /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(value);
          if (item?.type === PROPERTY_TYPES.DATE && value) {
            return moment(value, DEFAULTDATEFORMAT, true)?.isValid()
              ? moment(
                  value,
                  isDefaultDateFormat ? DEFAULTDATEFORMAT : DATE_ISO_FORMAT,
                )
              : '';
          }
          if (item?.type === PROPERTY_TYPES.TIME && value) {
            if (moment.isMoment(value)) return value;
            const time24 = moment(value, TIME_24_HOUR_FORMAT, true);
            if (time24?.isValid()) return time24;
            return moment(value, TIME_12_HOUR_FORMAT, true);
          }
          return value || null;
        };

        const result = data?.map((item) => {
          const rawValue = item?.registerLogDetailEntries?.value;
          let processedValue = processValue(item, rawValue);
          const fieldIndex = toCamelCase(item?.name);
          const formKey = `0_${fieldIndex}`;
          const localValue = currentFormValues?.[formKey];
          const isServerValueEmpty =
            processedValue === null ||
            processedValue === '' ||
            processedValue === undefined;
          const isLocalValuePresent =
            localValue !== undefined &&
            localValue !== null &&
            localValue !== '';
          if (isServerValueEmpty && isLocalValuePresent) {
            processedValue = localValue;
          }
          return {
            name: item?.name,
            index: fieldIndex,
            id: item?.registerLogDetailEntries?.id,
            value: processedValue,
            refId: item?.registerLogDetailEntries?.refIds,
            registerDetailId: item?.id,
            type: item?.type,
            options: item?.options,
            isRequired: item?.isRequired,
            updatedAt: item?.registerLogDetailEntries?.updatedAt,
            createdAt: item?.registerLogDetailEntries?.createdAt,
            comments: item?.registerLogDetailEntries?.comments || [],
            deletedAt: item?.deletedAt,
          };
        });
        if (scrollFlag) {
          const dataList = [...propertyDetails, ...result];
          setPropertyDetails(dataList);
          setScrollFlag(false);
        } else {
          const dataList = [...result];
          setPropertyDetails(dataList);
        }
        setHasMore(!!data?.length);
        setIsLoading(false);
      },
      onError() {},
    },
  );

  const [saveRegisterLogToDraft, { loading: saveRegisterLogToDraftLoading }] =
    useMutation(SAVE_REGISTER_LOG_TO_DRAFT, {
      onCompleted: (res) => {
        setIsDirty(false);
        setDetailsFormHasErrors(false);
        setTableFormHasErrors(false);
        const newEntries =
          res?.createRegisterLogStatusEntry?.registerLogFormFieldEntry;
        tableListRef?.current?.setTableData();
        if (tableListRef?.current?.resetModifiedFields) {
          tableListRef?.current?.resetModifiedFields();
        }
        if (tableListRef?.current?.resetFormChanged) {
          tableListRef?.current?.resetFormChanged();
        }
        if (newEntries && tableListRef?.current?.updateTableIds) {
          tableListRef?.current?.updateTableIds(newEntries);
        }
        getRegisterLogDetails({
          variables: {
            registerLogId,
            filter: {
              ...initialFilter,
              registerLogHistoryId: historyData?.isLatest
                ? undefined
                : historyData?.id,
              showAll,
            },
          },
        });
      },
      onError: () => {},
    });

  const [registerLogsExistInRegister] = useLazyQueryWithCallbacks(
    REGISTER_LOGS_EXIST_BY_REGISTER,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        setRegisterHasLogs(
          res?.registerLogExistByRegister?.registerLogExistByRegister,
        );
      },
    },
  );

  const [updateRegister] = useMutation(UPDATE_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      getRegister({ variables: { getRegisterId: registerId } });
      setIsDirty(false);
      checkDetailsFormErrors();
    },
  });

  const handleRegisterLogDetailRefetch = async () => {
    await getRegisterLogDetails({
      variables: {
        registerLogId,
        filter: {
          ...initialFilter,
          registerLogHistoryId: historyData?.isLatest
            ? undefined
            : historyData?.id,
          showAll,
        },
      },
    });
  };

  const [updateRegisterLogDetail] = useMutation(
    UPDATE_REGISTER_LOG_DETAIL_ENTRY_LIST,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        showDetailPrompt.current = false;
        showRouterPrompt(
          showDetailPrompt?.current || showTableFieldsPrompt?.current,
        );
        handleHisotryRefetch();
        const currentFieldsValues = form?.getFieldsValue();
        const fieldsResetData = Object?.keys(currentFieldsValues)?.map(
          (key) => ({
            name: key,
            touched: false,
            value: currentFieldsValues?.[key],
          }),
        );
        form?.setFields(fieldsResetData);
        // Only set isDirty if there are actual unsaved changes
        const isTableDirty = tableListRef?.current?.isTableDirty?.() ?? false;
        const hasUnsavedChanges = showDetailPrompt?.current || showTableFieldsPrompt?.current || isTableDirty;
        setIsDirty(hasUnsavedChanges);
        checkDetailsFormErrors();
        handleRegisterLogDetailRefetch();
      },
    },
  );

  const [getAffectedLogsCount] = useLazyQueryWithCallbacks(GET_AFFECTED_LOGS_COUNT, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(res) {
      setAffectedLogsCount(res?.getAffectedLogsCount?.count || 0);
    },
  });

  useEffect(() => {
    refetchRef.current = {
      getRegister: () =>
        getRegister({ variables: { getRegisterId: registerId } }),
    };
  }, [registerId]);

  // Refetch when registerId changes (for edit-control route)
  useEffect(() => {
    if (
      location?.pathname ===
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      getRegister({ variables: { getRegisterId: registerId } });
      registerLogsExistInRegister({
        variables: {
          registerId,
        },
      });
    }
  }, [registerId]);

  const transformDataToColumns = (data) => {
    if (!data || !data.length) return [];
    const sampleObject = data[0];

    const columns = Object.keys(sampleObject)
      .filter((key) => !['registerLogId', 'createdAt'].includes(key))
      .map((key) => ({
        title: key,
        dataIndex: key,
        key,
        index: key,
      }));

    return columns;
  };

  useEffect(() => {
    if (registerDetailsData?.length) {
      const columns = transformDataToColumns(registerDetailsData);
      setTableColumns(columns);
    }
  }, [registerDetailsData]);

  useEffect(() => {
    if (
      location?.pathname !==
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control` &&
      registerLog?.id
    ) {
      setIsLoading(true);
      getRegisterLogDetails({
        variables: {
          registerLogId,
          filter: {
            ...initialFilter,
            registerLogHistoryId: historyData?.isLatest
              ? undefined
              : historyData?.id,
            showAll,
          },
        },
      });
    }
  }, [historyData?.id, registerId, showAll, registerLog]);

  useEffect(() => {
    if (
      location?.pathname ===
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      getRegister({ variables: { getRegisterId: registerId } });
      registerLogsExistInRegister({
        variables: {
          registerId,
        },
      });
      setIsEditControl(true);
    }
  }, []);

  useEffect(() => {
    // Filter out deleted fields
    const activeFields = registerDetailsData?.registerFormFields?.filter(
      (field) => !field?.deletedAt,
    );
    const updatedFields = map(activeFields, (field) => ({
      ...field,
      name: field?.name?.toUpperCase(),
      dataIndex: toCamelCase(field?.name),
      index: toCamelCase(field?.name),
    }));
    setTableColumns(updatedFields);
  }, [registerDetailsData]);

  // Update isDisabled based on historyData changes
  useEffect(() => {
    if (
      location?.pathname !==
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      if (
        includes(
          [
            REGISTER_LOG_STATUS.COMPLETED,
            REGISTER_LOG_STATUS.DRAFT,
            REGISTER_LOG_STATUS.REDO_DRAFT,
          ],
          registerLog?.status,
        ) ||
        (registerLog?.status === REGISTER_LOG_STATUS.REDO &&
          historyData?.isLatest) ||
        (getCurrentUserRole() === USER_ROLES.SYSTEM_ADMIN &&
          registerLog?.status === REGISTER_LOG_STATUS.APPROVED &&
          historyData?.isLatest)
      ) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    }
  }, [
    historyData,
    registerDetailsData?.status,
    location?.pathname,
    projectId,
    registerId,
  ]);
  useEffect(() => {
    if (propertyDetails?.length && !isEditControl) {
      const transformedProperty = map(propertyDetails, (item) => ({
        ...item,
        id: item?.registerDetailId,
        index: item?.index || toCamelCase(item?.name),
      }));
      const initialLabels = {};
      const touchedFields = new Set();

      const initialValues = transformedProperty.reduce((acc, item) => {
        let { value } = item;

        if (item?.type === PROPERTY_TYPES?.EQC) {
          const labelString = Array?.isArray(value)
            ? value?.join(',')
            : value || '';
          initialLabels[item?.index] = labelString;
          const ids = item?.refId || [];
          const labels = labelString ? labelString?.split(',') : [];
          ids?.forEach((id, idx) => {
            if (labels[idx]) {
              eqcIdToLabelMap.current[id] = labels?.[idx];
            }
          });
          value = item?.refId || undefined;
        } else if (
          item?.type === PROPERTY_TYPES.CHECKBOXES &&
          typeof value === 'string' &&
          value
        ) {
          value = value.split(',');
        }
        const formFieldName = `0_${item.index}`;
        acc[formFieldName] = value || undefined;
        return acc;
      }, {});

      const currentFormValues = form?.getFieldsValue();
      const mergedValues = { ...initialValues };
      transformedProperty?.forEach((item) => {
        const key = `0_${item?.index}`;
        if (item?.type === PROPERTY_TYPES?.CALCULATION) {
          return;
        }
        if (form?.isFieldTouched(key)) {
          touchedFields?.add(key);
          if (currentFormValues?.[key] !== undefined) {
            mergedValues[key] = currentFormValues?.[key];
          }
        }
      });
      form?.setFieldsValue(mergedValues);

      setTimeout(() => {
        const allFields = transformedProperty?.map(
          (item) => `0_${item?.index}`,
        );
        const fieldsToReset = allFields
          ?.filter((name) => !touchedFields?.has(name))
          ?.map((name) => ({ name, touched: false }));
        if (fieldsToReset?.length > 0) {
          form?.setFields(fieldsToReset);
        }
        const calculationFields = transformedProperty.filter(
          (item) => item?.type === PROPERTY_TYPES.CALCULATION,
        );

        if (calculationFields?.length) {
          calculationFields.forEach((calcField) => {
            const calcFieldName = `0_${calcField?.index}`;

            const calculatedValue = calculateFieldValue(
              calcFieldName,
              form,
              transformedProperty,
            );

            if (calculatedValue && calculatedValue !== '-') {
              form?.setFieldValue(calcFieldName, calculatedValue);
            }
          });
        }
      }, 100);
    }
  }, [propertyDetails, isEditControl, form]);

  const handleRegisterRefetch = () => {
    getRegister({ variables: { getRegisterId: registerId } });
  };

  const handleRegisterArchive = () => {
    setConfirmationType(CONFIRMATION_TYPES.ARCHIVE);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (
      propertyDetails?.length === 1 &&
      propertyDetails[0]?.isRequired === false
    ) {
      message.destroy();
      message.error('There must be at-least 1 required detail field!');
      return;
    }
    setConfirmationType(CONFIRMATION_TYPES.SAVE);
    setShowModal(true);
  };

  const handleAssignApprovers = async () => {
    if (registerData?.status === REGISTER_STATUS_LABEL.LIVE) {
      await getAffectedLogsCount({ variables: { registerId } });
    }
    setShowApprovalModal(true);
  };

  const handleSeekApproval = () => {
    setShowSeekApprovalModal(true);
  };

  const handleSeekApprovalComplete = () => {
    handleHisotryRefetch();
  };

  const getLevelWiseApprovers = (approvers) => {
    const levelWiseApprovers = groupBy(approvers, (b) => {
      return b?.levelNumber;
    });
    return map(Object.keys(levelWiseApprovers), (data) => {
      return (
        <Descriptions.Item
          key={data}
          label={`${levelWiseApprovers?.[data]?.[0]?.levelName}:`}
          span={3}
        >
          {map(levelWiseApprovers?.[data], (approver) => {
            return (
              approver?.externalUserApprover?.name ||
              approver?.internalUserApprover?.name
            );
          }).join(', ')}
        </Descriptions.Item>
      );
    });
  };

  const renderApprovers = () => {
    const approvers = registerData?.approvers;
    return (
      approvers?.length > 0 && (
        <div className="ml-10">
          {isDesktopViewport ? (
            <CommonPopover
              classNames={{
                root: 'checklist-approvers-popup',
                container: 'checklist-approvers-container',
              }}
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.querySelector('.register-module')
              }
              title="Approvers info:"
              trigger="hover"
              placement="left"
              destroyOnHidden
              content={
                <div onClick={(e) => e.stopPropagation()}>
                  <Descriptions
                    column={3}
                    layout="vertical"
                    colon={false}
                    className="inner"
                  >
                    {getLevelWiseApprovers(approvers)}
                  </Descriptions>
                </div>
              }
            >
              <PersonDone
                className="checklist-approvers"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </CommonPopover>
          ) : (
            <PersonDone
              className="checklist-approvers"
              onClick={(e) => {
                e.stopPropagation();
                setDrawerData(approvers);
                setOpen(true);
              }}
            />
          )}
        </div>
      )
    );
  };

  const getProcessedPayload = useCallback(
    (item, fieldValue) => {
      let processedValue = fieldValue;
      let extraPayload = {};
      if (item?.type === PROPERTY_TYPES?.NOMENCLATURE) {
        if (nomenclatureStatus) {
          if (processedValue) {
            const isObject =
              typeof processedValue === 'object' && processedValue !== null;
            const valueLabel = isObject
              ? processedValue?.label
              : processedValue;
            let refIdsToSend = [];
            if (isObject && processedValue?.value) {
              refIdsToSend = [String(processedValue?.value)];
            } else {
              const existingRefId = item?.refId;
              if (Array?.isArray(existingRefId)) {
                refIdsToSend = existingRefId?.map(String);
              } else if (existingRefId) {
                refIdsToSend = [String(existingRefId)];
              }
            }
            processedValue = valueLabel;
            extraPayload = {
              refType: PROPERTY_TYPES?.NOMENCLATURE,
              refId: refIdsToSend,
            };
          } else {
            processedValue = '';
          }
        }
      } else if (item?.type === PROPERTY_TYPES?.EQC) {
        if (Array?.isArray(processedValue) && processedValue?.length > 0) {
          const labelString = processedValue
            ?.map((id) => eqcIdToLabelMap?.current?.[id])
            ?.filter(
              (label) => label !== undefined && label !== null && label !== '',
            )
            ?.join(',');
          extraPayload = {
            refType: PROPERTY_TYPES?.EQC,
            refId: processedValue?.map(String),
          };
          processedValue = labelString;
        } else {
          processedValue = '';
        }
      } else if (item?.type === PROPERTY_TYPES?.DATE && processedValue) {
        if (moment?.isMoment(processedValue)) {
          processedValue = processedValue?.format(DEFAULTDATEFORMAT);
        }
      } else if (item?.type === PROPERTY_TYPES?.TIME && processedValue) {
        if (moment?.isMoment(processedValue)) {
          processedValue = processedValue?.format(TIME_24_HOUR_FORMAT);
        } else {
          processedValue = moment(new Date(processedValue))?.format(
            TIME_24_HOUR_FORMAT,
          );
        }
      } else if (
        item?.type === PROPERTY_TYPES?.CHECKBOXES &&
        Array?.isArray(processedValue)
      ) {
        processedValue = processedValue?.join(',');
      }
      if (typeof processedValue === 'string') {
        processedValue = processedValue?.trim();
      }
      processedValue =
        processedValue === undefined || processedValue === null
          ? ''
          : String(processedValue);

      return { value: processedValue, ...extraPayload };
    },
    [nomenclatureStatus],
  );

  const handleDetailBlur = useCallback(
    async (field) => {
      try {
        const formFieldName = `0_${field}`;
        await form?.validateFields([formFieldName]);

        // Filter out deleted fields before processing
        const activePropertyDetails = propertyDetails?.filter(
          (item) => !item?.deletedAt,
        );

        // Recalculate all calculation fields BEFORE getting form values
        // This ensures calculations are up-to-date before API call
        const transformedProperty = map(activePropertyDetails, (item) => ({
          ...item,
          id: item?.registerDetailId,
          index: item?.index || toCamelCase(item?.name),
        }));

        const calculationFields = transformedProperty?.filter(
          (item) => item?.type === PROPERTY_TYPES.CALCULATION,
        );

        if (calculationFields?.length) {
          calculationFields?.forEach((calcField) => {
            const calcFieldName = `0_${calcField?.index}`;
            const calculatedValue = calculateFieldValue(
              calcFieldName,
              form,
              transformedProperty,
            );
            form?.setFieldValue(calcFieldName, calculatedValue);
          });
        }

        const detailValues = form?.getFieldsValue();
        const allDetailFieldNames = activePropertyDetails?.map(
          (item) => `0_${item?.index}`,
        );
        await form?.validateFields(allDetailFieldNames);

        // Check if all required AND enabled fields are filled
        const allRequiredFieldsFilled = activePropertyDetails?.every((item) => {
          // Skip non-required fields
          if (!item?.isRequired) return true;

          // Skip disabled fields (calculation fields are always disabled/read-only)
          if (item?.type === PROPERTY_TYPES?.CALCULATION) return true;

          const itemFormFieldName = `0_${item?.index}`;
          const fieldValue = detailValues?.[itemFormFieldName];

          // Check if field has a value
          if (
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === ''
          ) {
            return false;
          }

          // For date fields, check if moment object is valid
          if (item?.type === PROPERTY_TYPES?.DATE) {
            if (!moment?.isMoment(fieldValue) || !fieldValue?.isValid()) {
              return false;
            }
          }

          // For time fields, check if moment object is valid
          if (item?.type === PROPERTY_TYPES?.TIME) {
            if (!moment?.isMoment(fieldValue) || !fieldValue?.isValid()) {
              return false;
            }
          }

          // For arrays (checkboxes), check if not empty
          if (Array?.isArray(fieldValue) && fieldValue?.length === 0) {
            return false;
          }

          return true;
        });
        // Only proceed if all required fields are filled
        if (!allRequiredFieldsFilled) {
          showDetailPrompt.current = false;
          showRouterPrompt(false);
          return;
        }
        // Only create entries for the changed field and any calculation fields
        const changedFieldDetail = activePropertyDetails?.find(
          (item) => (item?.index || toCamelCase(item?.name)) === field,
        );

        const calculationFieldDetails = activePropertyDetails?.filter(
          (item) => item?.type === PROPERTY_TYPES?.CALCULATION,
        );

        // Combine changed field with calculation fields, removing duplicates
        const fieldsToUpdate = [changedFieldDetail, ...calculationFieldDetails]
          ?.filter(
            (item, index, self) =>
              item &&
              self?.findIndex(
                (i) => i?.registerDetailId === item?.registerDetailId,
              ) === index,
          );

        const entries = fieldsToUpdate
          ?.map((item) => {
            const itemFormFieldName = `0_${item?.index}`;
            const fieldValue = detailValues?.[itemFormFieldName];

            const { value, ...extraPayload } = getProcessedPayload(
              item,
              fieldValue,
            );

            return {
              registerDetailId: item?.registerDetailId,
              id: item?.id,
              value,
              ...extraPayload,
            };
          })
          ?.filter(Boolean);
        // Send only changed field and calculation fields
        if (entries?.length > 0 && allRequiredFieldsFilled) {
          await updateRegisterLogDetail({
            variables: { registerLogId, entries },
          });

          // Track which entries were modified by the user in this session
          // This is used to show blue badges only on actually modified fields
          fieldsToUpdate?.forEach((item) => {
            if (item?.registerDetailId) {
              modifiedDetailIds?.current?.add(item?.registerDetailId);
            }
          });

          // Update display values only for fields that were sent
          fieldsToUpdate?.forEach((item) => {
            const itemFormFieldName = `0_${item?.index}`;
            const savedValue = detailValues?.[itemFormFieldName];
            let displayValue = savedValue;

            if (item?.type === PROPERTY_TYPES?.EQC) {
              displayValue = savedValue ? savedValue?.split(',') : [];
            } else if (item?.type === PROPERTY_TYPES?.DATE && savedValue) {
              displayValue = moment(
                savedValue,
                DEFAULTDATEFORMAT,
                true,
              )?.isValid()
                ? moment(savedValue, DEFAULTDATEFORMAT)
                : savedValue;
            } else if (item?.type === PROPERTY_TYPES.TIME && savedValue) {
              const time24 = moment(savedValue, TIME_24_HOUR_FORMAT, true);
              if (time24?.isValid()) {
                displayValue = time24;
              } else {
                const time12 = moment(savedValue, TIME_12_HOUR_FORMAT, true);
                displayValue = time12?.isValid() ? time12 : savedValue;
              }
            } else if (typeof displayValue === 'string') {
              displayValue = savedValue?.trim?.() ?? savedValue;
            }

            form?.setFieldValue(itemFormFieldName, displayValue);
          });

          showDetailPrompt.current = false;
          showRouterPrompt(false);
        }
      } catch (error) {
        showDetailPrompt.current = true;
        showRouterPrompt(true);
        return error;
      }
    },
    [
      form,
      propertyDetails,
      registerLogId,
      updateRegisterLogDetail,
      getProcessedPayload,
    ],
  );
  const debouncedHandleDetailBlur = useMemo(
    () => debounce(handleDetailBlur, 500),
    [handleDetailBlur],
  );

  useEffect(() => {
    return () => {
      debouncedHandleDetailBlur.cancel();
    };
  }, [debouncedHandleDetailBlur]);

  const handleSaveAsDraft = async (shouldRedirect = false) => {
    if (tableListRef?.current?.cancelPendingSave) {
      tableListRef?.current?.cancelPendingSave();
    }
    // Filter out deleted fields before processing
    const activePropertyDetails = propertyDetails?.filter(
      (item) => !item?.deletedAt,
    );
    const allDetailFieldNames = activePropertyDetails?.map(
      (item) => item?.index,
    );
    const clearedDetailFields = allDetailFieldNames?.map((name) => ({
      name,
      errors: [],
    }));
    form?.setFields(clearedDetailFields);
    if (tableListRef?.current?.clearFormErrors) {
      tableListRef?.current?.clearFormErrors();
    }
    try {
      const detailsFormValues = await form?.getFieldsValue();
      const processedDetails = activePropertyDetails?.map((item) => {
        const value = detailsFormValues?.[`0_${item?.index}`];

        const { value: processedValue, ...extraPayload } = getProcessedPayload(
          item,
          value,
        );

        return {
          registerDetailId: item?.registerDetailId,
          value: processedValue,
          ...extraPayload,
        };
      });

      const tableFieldEntries = tableListRef?.current?.getChangedTableData();

      const draftPayload = {
        registerId,
        data: {
          registerLogId,
          registerLogDetailEntries: {
            registerDetailValues: processedDetails,
          },
          registerLogFormFieldEntries: {
            filling: null,
            tableEntries: tableFieldEntries,
          },
        },
      };
      const res = await saveRegisterLogToDraft({ variables: draftPayload });
      if (shouldRedirect && res?.data) {
        setTimeout(() => {
          navigate(
            `${ROUTES?.PROJECTS}/${projectId}/${TAB_KEYS?.REGISTER}/${registerId}`,
          );
        }, 200);
      }
      return res;
    } catch {
      // Silent error handling
    }
  };

  const handleTableRouterPromptUpdate = (value) => {
    showTableFieldsPrompt.current = value;
  };

  const hasDeletedEntries = useMemo(() => {
    const hasDeletedDetails = propertyDetails?.some(
      (detail) => detail?.deletedAt,
    );
    const hasDeletedColumns = tableHeaders?.some((header) => header?.deletedAt);
    return hasDeletedDetails || hasDeletedColumns;
  }, [propertyDetails, tableHeaders]);

  const renderProperties = (property) => {
    // Transform property data to include index and 0_ prefix for calculateFieldValue compatibility
    // IMPORTANT: Use registerDetailId as id for calculateFieldValue UUID matching
    const transformedProperty = map(property, (item) => ({
      ...item,
      id: item?.registerDetailId, // Map registerDetailId to id for UUID matching
      index: item?.index || toCamelCase(item?.name),
    }));
    // Separate active and deleted fields
    const activeFields = transformedProperty?.filter(
      (detail) => !detail?.deletedAt,
    );
    const deletedFields = transformedProperty?.filter(
      (detail) => detail?.deletedAt,
    );
    // Only include deleted fields if showDeletedEntries is true
    const sortedProperty = showDeletedEntries
      ? [...activeFields, ...deletedFields]
      : activeFields;

    const result = sortedProperty.reduce((acc, item) => {
      let { value } = item;
      if (
        item?.type === PROPERTY_TYPES.CHECKBOXES &&
        typeof value === 'string' &&
        value
      ) {
        value = value.split(',');
      }
      if (
        item?.type === PROPERTY_TYPES?.EQC &&
        typeof value === 'string' &&
        value
      ) {
        value = value?.split(',');
      }
      acc[`0_${item.index}`] = value || undefined;
      return acc;
    }, {});

    return map(sortedProperty, (detail) => {
      const options = detail?.options?.map((option) => ({
        label: option,
        value: option,
      }));
      const formFieldName = `0_${detail?.index}`;
      let defaultEqcOptions = [];
      if (detail?.type === PROPERTY_TYPES?.EQC) {
        let ids = [];
        if (Array?.isArray(detail?.refId)) {
          ids = detail?.refId;
        } else if (detail?.refId) {
          ids = [detail?.refId];
        }
        let labels = [];
        const passedLabels = location?.state?.eqcLabels?.[detail?.index];
        if (passedLabels) {
          labels = passedLabels?.split(',');
        } else if (Array?.isArray(detail?.value)) {
          labels = detail?.value;
        } else if (typeof detail?.value === 'string') {
          labels = detail?.value?.split(',');
        }
        defaultEqcOptions = ids?.map((id, index) => ({
          value: id,
          label: labels?.[index] || id,
          // eslint-disable-next-line no-undef
          url: `${window?.location?.origin}/projects/${projectId}/eqc/${id}`,
        }));
      }

      if (!isEditControl) {
        const hasComments = detail?.comments?.length > 0;
        const shouldShowCommentIcon =
          includes(
            [REGISTER_LOG_STATUS.REDO, REGISTER_LOG_STATUS.APPROVED],
            historyData?.approvalStatus,
          ) && hasComments;
        const isDeleted = detail?.deletedAt;
        const showUpdateBadge = isFieldUpdatedAfterHistory(
          detail?.updatedAt,
          detail?.registerDetailId,
        );

        let fieldNameDisplay = detail?.name;
        if (isDeleted) {
          fieldNameDisplay = `${detail?.name} (Deleted)`;
        } else if (showUpdateBadge) {
          fieldNameDisplay = (
            <Badge dot offset={[4, 5]} color="#1890ff">
              {detail?.name}
            </Badge>
          );
        }

        const fieldContent = (
          <Form.Item key={formFieldName} noStyle shouldUpdate>
            {() => (
              <RenderFields
                fieldType={detail?.type}
                fieldIndex={formFieldName}
                fieldName={fieldNameDisplay}
                fieldIsRequired={detail?.isRequired}
                initialFieldValue={result[formFieldName]}
                fieldDisabled={
                  isDisabled || !isProjectAdminOrInspector || isDeleted
                }
                formClassName={
                  !isDesktopViewport ? 'width-percent-100' : 'width-200 mr-20'
                }
                showCommentIcon={shouldShowCommentIcon}
                hasComments={hasComments}
                onCommentClick={() => {
                  if (hasComments) {
                    setSelectedDetailsComment(detail?.comments?.[0]);

                    setShowDetailsCommentModal(true);
                  }
                }}
                onTextChange={(e) => {
                  const { value } = e?.target;
                  form.setFieldsValue({ [formFieldName]: value || undefined });
                  debouncedHandleDetailBlur(detail?.index);
                }}
                onNumericChange={(e) => {
                  const { value } = e?.target;
                  const formattedValue = formatNumericInput(value);
                  form.setFieldsValue({
                    [formFieldName]: formattedValue,
                  });

                  // Trigger recalculation for calculation fields
                  const calculationFields = transformedProperty?.filter(
                    (field) => field?.type === PROPERTY_TYPES.CALCULATION,
                  );

                  if (calculationFields?.length) {
                    calculationFields?.forEach((calcField) => {
                      const calcFieldName = `0_${calcField?.index}`;
                      const calculatedValue = calculateFieldValue(
                        calcFieldName,
                        form,
                        transformedProperty,
                      );
                      form?.setFieldValue(calcFieldName, calculatedValue);
                    });
                  }

                  debouncedHandleDetailBlur(detail?.index);
                }}
                optionsOpen={isOptionsFocused === detail?.index}
                onOptionsFocus={() => setIsOptionsFocused(detail?.index)}
                onOptionChange={(value) => {
                  const newValue = value?.length > 0 ? value : undefined;
                  form.setFieldsValue({ [formFieldName]: newValue });
                  handleDetailBlur(detail?.index);
                }}
                onOptionOpenChange={(e) => {
                  if (!e) {
                    setIsOptionsFocused(null);
                  } else {
                    setIsOptionsFocused(detail?.index);
                  }
                }}
                options={options}
                onOptionBlur={() => setIsOptionsFocused(null)}
                getPopupContainer={() =>
                  // eslint-disable-next-line no-undef
                  document?.querySelector('.ant-card-body')
                }
                multiSelectOpen={isMultiSelectOptionsFocused === detail?.index}
                onMultiSelectFocus={() =>
                  setIsMultiSelectOptionsFocused(detail?.index)
                }
                onMultiSelectBlur={() => setIsMultiSelectOptionsFocused(null)}
                onMultiSelectOpenChange={(e) => {
                  if (!e) {
                    setIsMultiSelectOptionsFocused(null);
                  } else {
                    setIsMultiSelectOptionsFocused(detail?.index);
                  }
                }}
                onMultiSelectChange={(value) => {
                  const newValue = value?.length > 0 ? value : undefined;
                  form.setFieldsValue({ [formFieldName]: newValue });
                  handleDetailBlur(detail?.index);
                }}
                dateOpen={isDateFocused === detail?.index}
                onDateFocused={() => setIsDateFocused(detail?.index)}
                onDateOpenChange={(e) => {
                  if (!e) {
                    setIsDateFocused();
                    handleDetailBlur(detail?.index);
                  }
                }}
                onDateChange={async (date) => {
                  try {
                    form.setFieldsValue({ [formFieldName]: date || undefined });
                    await form.validateFields([formFieldName]);
                    handleDetailBlur(detail?.index);
                  } catch (error) {
                    showDetailPrompt.current = true;
                    showRouterPrompt(true);
                    return error;
                  }
                }}
                timeOpen={isTimeFocused === detail?.index}
                onTimeFocused={() => setIsTimeFocused(detail?.index)}
                onTimeOpenChange={(e) => {
                  if (!e) {
                    setIsTimeFocused();
                    handleDetailBlur(detail?.index);
                  }
                }}
                onTimeChange={async (time) => {
                  try {
                    form?.setFieldsValue({
                      [formFieldName]: time || undefined,
                    });
                    handleDetailBlur(detail?.index);
                  } catch (error) {
                    showDetailPrompt.current = true;
                    showRouterPrompt(true);
                    return error;
                  }
                }}
                calculateFieldValue={() =>
                  calculateFieldValue(formFieldName, form, transformedProperty)
                }
                onCalculationChange={(e) => {
                  const { value } = e?.target;
                  form.setFieldsValue({ [formFieldName]: value });
                }}
                onCalculationBlur={() => {
                  const calculatedValue = calculateFieldValue(
                    formFieldName,
                    form,
                    transformedProperty,
                  );
                  form?.setFieldValue(formFieldName, calculatedValue);
                  handleDetailBlur(detail?.index);
                }}
                onEqcChange={(ids, selectedOptions) => {
                  const newValue = ids?.length > 0 ? ids : undefined;
                  if (selectedOptions?.length > 0) {
                    selectedOptions?.forEach((opt) => {
                      if (opt?.value && opt?.label) {
                        eqcIdToLabelMap.current[opt?.value] = opt?.label;
                      }
                    });
                  }
                  form?.setFieldsValue({ [formFieldName]: newValue });
                }}
                defaultOptions={defaultEqcOptions}
                onEqcBlur={() => {
                  handleDetailBlur(detail?.index);
                }}
                onNomenclatureChange={(value) => {
                  form?.setFieldsValue({ [formFieldName]: value });
                  handleDetailBlur(detail?.index);
                }}
                nomenclatureStatus={nomenclatureStatus}
                historyData={historyData}
              />
            )}
          </Form.Item>
        );
        return isDeleted ? (
          <div className="deleted-field">{fieldContent}</div>
        ) : (
          fieldContent
        );
      }
    });
  };

  const renderPropertiesDetails = () => {
    return (
      <RegisterDetailsTable
        isEditable={isEditable}
        propertyDetails={propertyDetails}
        setPropertyDetails={setPropertyDetails}
        setTableColumns={setTableColumns}
        tableColumns={tableColumns}
        isAuditor={isAuditor}
        handleRegisterRefetch={handleRegisterRefetch}
      />
    );
  };

  const handleScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;
    if (scrolledToBottom && hasMore && !loading && !scrollFlag) {
      if (propertyDetails?.length >= totalDetailsCount) return;
      setScrollFlag(true);
      getRegisterLogDetails({
        variables: {
          registerLogId,
          filter: {
            ...initialFilter,
            registerLogHistoryId: historyData?.isLatest
              ? undefined
              : historyData?.id,
            skip: propertyDetails?.length,
            showAll,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (registerId) {
      const storageKey = getStorageKey(registerId);
      try {
        // eslint-disable-next-line no-undef
        const storedValue = sessionStorage?.getItem(storageKey);
        if (storedValue) {
          setSelectedAggregates(JSON?.parse(storedValue));
        } else {
          setSelectedAggregates({});
        }
      } catch {
        setSelectedAggregates({});
      }
    }
  }, [registerId]);

  useEffect(() => {
    if (registerId) {
      const storageKey = getStorageKey(registerId);
      try {
        if (Object?.keys(selectedAggregates)?.length > 0) {
          // eslint-disable-next-line no-undef
          sessionStorage?.setItem(
            storageKey,
            JSON?.stringify(selectedAggregates),
          );
        } else {
          // eslint-disable-next-line no-undef
          sessionStorage?.removeItem(storageKey);
        }
      } catch {
        // Silent error handling
      }
    }
  }, [selectedAggregates, registerId]);

  const renderDownloadCsvButton = () => {
    return (
      <div
        className="d-flex width-percent-100"
        onClick={async () => {
          const token = await getValidToken();
          const aggregatedFields =
            transformAggregatesForExportURL(selectedAggregates);
          let url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/export-csv/report/register/entries?access_token=Bearer ${token}&tenantId=${tenantId}&registerId=${mainRegisterId}&registerLogId=${registerLogId}`;
          if (searchValue) {
            url += `&search=${searchValue}`;
          }
          if (Object?.keys(aggregatedFields)?.length > 0) {
            const aggregateParamsArray = Object?.entries(
              aggregatedFields,
            )?.map(([operation, fieldIds]) => {
              const idString = fieldIds?.join(',');
              return `${operation?.toLocaleLowerCase()}=${idString}`;
            });
            url += `&${aggregateParamsArray?.join('&')}`;
          }
          if (showDeletedEntries) {
            url += `&showDeletedFields=${showDeletedEntries}`;
          }
          Event(GA_EVENT.EXPORT_PROJECT_REGISTER_CSV, {
            label: GA_LABEL.EXPORT_PROJECT_REGISTER_CSV,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            project_register_id: registerId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          // eslint-disable-next-line no-undef
          window.open(url, '_blank');
        }}
      >
        CSV
      </div>
    );
  };

  // Render history summary card
  const renderHistorySummary = () => {
    if (isEditControl || !registerLogId || historyOptions?.length === 0) {
      return null;
    }

    return (
      <Card className="register-log-history mb-24" loading={historyLoading}>
        <Row className="mt-1 mb-15" wrap={false}>
          <Col flex="auto">
            <div className="d-flex align-center gap-10">
              {historyOptions?.length > 0 ? (
                <CommonSelect
                  id="register-history-select"
                  onChange={handleHistorySelect}
                  className={`${
                    isDesktopViewport ? 'width-400' : 'width-percent-100'
                  } mr-10`}
                  value={selectedHistoryId}
                  showSearch={false}
                  optionFilterProp="children"
                  loading={historyLoading}
                  options={map(
                    historyOptions,
                    ({ createdAt, actor, approvalStatus, id, levelName }) => {
                      return {
                        key: id,
                        value: id,
                        label: (
                          <div className="whitespace-wrap">
                            {actor?.name} -{' '}
                            {moment(createdAt)?.format(DEFAULTDATETIMEFORMAT)} -{' '}
                            {titleCase(
                              REGISTER_LOG_STATUS[approvalStatus] ||
                                approvalStatus,
                            )}
                            {levelName && ` (${levelName})`}
                          </div>
                        ),
                      };
                    },
                  )}
                />
              ) : (
                <Col xs="auto">
                  <h5 className="inline text-muted mr-3">Log Summary</h5>
                </Col>
              )}
            </div>
          </Col>
        </Row>
        <Row
          className="mb-15"
          gutter={isDesktopViewport ? 32 : [16, 32]}
          justify="space-between"
          wrap={!isDesktopViewport}
        >
          <Col span={isDesktopViewport ? 6 : 12}>
            <Space orientation="vertical" size={12}>
              <div className="text-secondary fw-medium">Created By</div>
              <EllipsisText text={registerLog?.creator?.name || '-'} />
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 12}>
            <Space orientation="vertical" size={12}>
              <div className="text-secondary fw-medium">Created At</div>
              <div>
                {historyData?.createdAt
                  ? moment(historyData?.createdAt)?.format(DATETIMEWITHDIVIDE)
                  : '-'}
              </div>
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 12}>
            <Space orientation="vertical" size={12}>
              <div className="text-secondary fw-medium">Approval Status</div>
              {historyData?.approvalStatus ? (
                <Tag
                  className={
                    STAGE_STATUS_CLASSNAME[historyData?.approvalStatus]
                  }
                >
                  {titleCase(
                    REGISTER_LOG_STATUS[historyData?.approvalStatus] ||
                      historyData?.approvalStatus,
                  )}
                </Tag>
              ) : (
                '-'
              )}
            </Space>
          </Col>
          <Col span={isDesktopViewport ? 6 : 12}>
            <Space orientation="vertical" size={12}>
              <div className="text-secondary fw-medium">Level</div>
              <div>{historyData?.levelName || '-'}</div>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="mb-20">
      {renderHistorySummary()}
      <CommonCard className="checklist-details mb-24" loading={isLoading}>
        <Row justify="space-between" align="middle">
          <Col span={isDesktopViewport && isEditControl ? 18 : 24}>
            <div
              className={`checklist-basic-details d-flex align-center ${!isDesktopViewport && 'mb-10'}`}
            >
              {isEditControl && isEditable ? (
                <Form layout="vertical" form={form}>
                  <Form.Item
                    name="registerName"
                    rules={[
                      required,
                      characterWithoutWhiteSpace,
                      {
                        max: 250,
                        message: 'Name cannot be more than 250 characters',
                      },
                    ]}
                    label="Register Name"
                  >
                    <Input
                      placeholder="Enter Register Name"
                      allowClear
                      className={
                        !isDesktopViewport ? 'width-percent-100' : 'width-200'
                      }
                      disabled={isAuditor || isDisabled}
                      onBlur={async () => {
                        const value = form?.getFieldValue('registerName');
                        if (registerData?.name === value) return;
                        if (registerId && value) {
                          await updateRegister({
                            variables: {
                              updateRegisterId: registerId,
                              data: {
                                name: value,
                              },
                            },
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </Form>
              ) : (
                <>
                  {registerData?.name && (
                    <EllipsisText
                      className="h2-typography pb-10"
                      text={registerData?.name}
                    />
                  )}
                </>
              )}
            </div>
          </Col>
          {includes(
            [REGISTER_STATUS_LABEL.DRAFT, REGISTER_STATUS_LABEL.UNPUBLISHED],
            registerData?.status,
          ) && isDesktopViewport ? (
            <Col
              className="flex-vertical d-flex justify-end"
              span={isDesktopViewport ? 6 : null}
            >
              <div className="d-flex justify-end form-buttons">
                <CanPerform
                  action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
                  type={ALLOWED_ACTION_TYPE.BOTH}
                >
                  {renderApprovers()}
                  <Button
                    shape="round"
                    icon={<UserAddOutlined />}
                    className="assign-approvers-button d-flex"
                    onClick={handleAssignApprovers}
                  >
                    Assign Approvers
                  </Button>
                  {!registerHasLogs && (
                    <Button
                      shape="round"
                      icon={<DeleteButton width={32} height={32} />}
                      className="delete-button-checklist delete-button d-flex"
                      onClick={() => {
                        setShowDeleteRegisterModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    shape="round"
                    type="primary"
                    icon={<SaveOutline />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'edit-button-checklist d-flex',
                    )}
                    disabled={
                      !(
                        propertyDetails?.length > 0 &&
                        tableHeaders?.length > 0 &&
                        registerName
                      )
                    }
                    onClick={() => handleSave()}
                  >
                    Go Live
                  </Button>
                </CanPerform>
              </div>
            </Col>
          ) : null}
          {isEditControl && isDesktopViewport && (
            <Col
              className="flex-vertical d-flex justify-end"
              span={isDesktopViewport ? 6 : null}
            >
              <div className="d-flex justify-end">
                <div className="form-buttons width-percent-100">
                  <CanPerform
                    action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
                    type={ALLOWED_ACTION_TYPE.BOTH}
                  >
                    {registerData?.status === REGISTER_STATUS_LABEL.LIVE && (
                      <>
                        {renderApprovers()}
                        <Button
                          shape="round"
                          icon={<ArchiveOutline className="mr-5" />}
                          className={clsx(
                            'delete-button-checklist d-flex',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          onClick={() => handleRegisterArchive()}
                        >
                          Archive
                        </Button>
                        <Button
                          shape="round"
                          icon={<EditIcon />}
                          className={clsx(
                            'edit-button-checklist d-flex save-button',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          type="primary"
                          onClick={() => {
                            setConfirmationType(CONFIRMATION_TYPES.EDIT);
                            setShowModal(true);
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {registerData?.status ===
                      REGISTER_STATUS_LABEL.ARCHIVED && (
                      <Button
                        shape="round"
                        type="primary"
                        icon={<InboxOutlined />}
                        className={clsx(
                          !isDesktopViewport && 'justify-center fill-width',
                          'unarchive-button-checklist d-flex',
                        )}
                        onClick={() => {
                          setConfirmationType(CONFIRMATION_TYPES.UNARCHIVE);
                          setShowModal(true);
                        }}
                      >
                        Unarchive
                      </Button>
                    )}
                  </CanPerform>
                </div>
              </div>
            </Col>
          )}
          <Col span={!isEditControl && isDesktopViewport ? 18 : 24}>
            <div className="width-percent-100 header d-flex align-center justify-between mb-10">
              <Flex justify="space-between" className="width-percent-100">
                <h3 className="mb-0">Details</h3>
                {!isDesktopViewport && (
                  <CanPerform
                    action={ALLOWED_ACTION_KEYS?.ADD_REGISTER_LOG}
                    type={ALLOWED_ACTION_TYPE?.PROJECT}
                  >
                    <Button
                      type="primary"
                      shape="round"
                      size="middle"
                      className="save-button"
                      onClick={() => handleSaveAsDraft(true)}
                      loading={saveRegisterLogToDraftLoading}
                      disabled={
                        !isDirty ||
                        (!detailsFormHasErrors && !tableFormHasErrors)
                      }
                    >
                      Save as Draft
                    </Button>
                  </CanPerform>
                )}
              </Flex>
              <div className="register-header-buttons d-flex">
                <div id="register-details-btn" className="d-flex" />
                <div id="add-btn" />
              </div>
            </div>
          </Col>
          {!isEditControl && isDesktopViewport && (
            <Col
              className="flex-vertical d-flex justify-end"
              span={isDesktopViewport && 6}
            >
              <div className="d-flex justify-end align-center">
                <div className="form-buttons width-percent-100">
                  {historyData?.isLatest &&
                    registerLog?.status === REGISTER_LOG_STATUS.REDO && (
                      <>
                        {showAll ? (
                          <Tag
                            closable
                            onClose={() => {
                              setShowAll(false);
                            }}
                            onClick={() => {
                              setShowAll(false);
                            }}
                            className="show-all-data-tag"
                          >
                            Show All Entries
                          </Tag>
                        ) : (
                          <Button
                            type="default"
                            className="show-all-data-toggle"
                            onClick={() => {
                              setShowAll(true);
                            }}
                          >
                            Show All Entries
                          </Button>
                        )}
                      </>
                    )}
                  {hasDeletedEntries && (
                    <>
                      {showDeletedEntries ? (
                        <Tag
                          closable
                          onClose={() => {
                            setShowDeletedEntries(false);
                          }}
                          onClick={() => {
                            setShowDeletedEntries(false);
                          }}
                          className="show-all-data-tag"
                        >
                          Show Deleted Entries
                        </Tag>
                      ) : (
                        <Button
                          type="default"
                          className="show-all-data-toggle"
                          onClick={() => {
                            setShowDeletedEntries(true);
                          }}
                        >
                          Show Deleted Entries
                        </Button>
                      )}
                    </>
                  )}
                  {registerDetailsData?.approvers?.length > 0 &&
                    (registerLog?.status === REGISTER_LOG_STATUS.COMPLETED ||
                      (registerLog?.status === REGISTER_LOG_STATUS.REDO &&
                        registerLog?.isModifiedAfterRedo)) &&
                    registerLog?.needApproval &&
                    (!isEmpty(historyData) ? historyData?.isLatest : true) && (
                      <Button
                        shape="round"
                        type="primary"
                        className={clsx(
                          'seek-approval-button d-flex',
                          !isDesktopViewport &&
                            'width-percent-100 justify-center',
                        )}
                        disabled={
                          isDirty &&
                          (detailsFormHasErrors || tableFormHasErrors)
                        }
                        onClick={handleSeekApproval}
                      >
                        Seek for Approval
                      </Button>
                    )}
                  {(!isEmpty(historyData) ? historyData?.isLatest : true) && (
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'csv',
                            label: renderDownloadCsvButton(),
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement="bottom"
                    >
                      <Button
                        shape="round"
                        icon={<Import width="20px" />}
                        className={clsx(
                          'report-button d-flex',
                          !isDesktopViewport &&
                            'width-percent-100 justify-center',
                        )}
                      >
                        Download Log
                      </Button>
                    </Dropdown>
                  )}
                </div>
              </div>
              {isLogDraft && (
                <Flex justify="end" className="mt-16">
                  <Tag
                    className={
                      REGISTER_LOG_STATUS_CLASSNAME?.[
                        REGISTER_LOG_STATUS?.DRAFT
                      ]
                    }
                  >
                    {REGISTER_LOG_STATUS?.DRAFT}
                  </Tag>
                </Flex>
              )}
            </Col>
          )}
          <Col span={!isEditControl && isDesktopViewport ? 18 : 24}>
            {!loading &&
              !isEditControl &&
              !showAll &&
              totalDetailsCount === 0 &&
              registerLog?.status === REGISTER_LOG_STATUS.REDO && (
                <Alert
                  title='Only the approver&apos;s commented entries are shown. Tap "Show All Entries" to see the full register log'
                  type="warning"
                  showIcon
                  className={clsx(
                    'no-field-warning',
                    isDesktopViewport ? 'mt-10' : 'mb-10',
                  )}
                />
              )}
            <Form
              form={form}
              layout="vertical"
              className="d-flex"
              onFieldsChange={() => {
                setIsDirty(true);
                checkDetailsFormErrors();
              }}
            >
              <div
                className="d-flex width-percent-100 flex-wrap max-height-200 overflow-y-auto"
                onScroll={handleScroll}
              >
                {!isEditControl && renderProperties(propertyDetails)}
              </div>
            </Form>
          </Col>
          {!isEditControl && !isDesktopViewport && (
            <Col
              className="flex-vertical d-flex justify-end"
              span={!isDesktopViewport && 24}
            >
              <div className="d-flex justify-end">
                <div className="form-buttons width-percent-100 flex-vertical">
                  {historyData?.isLatest &&
                    registerLog?.status === REGISTER_LOG_STATUS.REDO && (
                      <>
                        {showAll ? (
                          <Tag
                            closable
                            icon={<UnorderedListOutlined />}
                            onClose={() => {
                              setShowAll(false);
                            }}
                            onClick={() => {
                              setShowAll(false);
                            }}
                            className={clsx(
                              'show-all-data-tag',
                              !isDesktopViewport && 'width-percent-100',
                            )}
                          >
                            Show All Entries
                          </Tag>
                        ) : (
                          <Button
                            type="default"
                            className={clsx(
                              'show-all-data-toggle mb-10',
                              !isDesktopViewport && 'width-percent-100',
                            )}
                            onClick={() => {
                              setShowAll(true);
                            }}
                            icon={<UnorderedListOutlined />}
                          >
                            Show All Entries
                          </Button>
                        )}
                      </>
                    )}
                  {hasDeletedEntries && (
                    <>
                      {showDeletedEntries ? (
                        <Tag
                          closable
                          icon={<UnorderedListOutlined />}
                          onClose={() => {
                            setShowDeletedEntries(false);
                          }}
                          onClick={() => {
                            setShowDeletedEntries(false);
                          }}
                          className={clsx(
                            'show-all-data-tag',
                            !isDesktopViewport && 'width-percent-100',
                          )}
                        >
                          Show Deleted Entries
                        </Tag>
                      ) : (
                        <Button
                          type="default"
                          className={clsx(
                            'show-all-data-toggle mb-10',
                            !isDesktopViewport && 'width-percent-100',
                          )}
                          onClick={() => {
                            setShowDeletedEntries(true);
                          }}
                          icon={<UnorderedListOutlined />}
                        >
                          Show Deleted Entries
                        </Button>
                      )}
                    </>
                  )}
                  {registerDetailsData?.approvers?.length > 0 &&
                    (registerLog?.status === REGISTER_LOG_STATUS.COMPLETED ||
                      (registerLog?.status === REGISTER_LOG_STATUS.REDO &&
                        registerLog?.isModifiedAfterRedo)) &&
                    registerLog?.needApproval &&
                    (!isEmpty(historyData) ? historyData?.isLatest : true) && (
                      <Button
                        shape="round"
                        type="primary"
                        className={clsx(
                          'seek-approval-button d-flex',
                          !isDesktopViewport &&
                            'width-percent-100 justify-center',
                        )}
                        disabled={
                          isDirty &&
                          (detailsFormHasErrors || tableFormHasErrors)
                        }
                        onClick={handleSeekApproval}
                      >
                        Seek for Approval
                      </Button>
                    )}
                  {(!isEmpty(historyData) ? historyData?.isLatest : true) && (
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'csv',
                            label: renderDownloadCsvButton(),
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement="bottom"
                    >
                      <Button
                        shape="round"
                        icon={<Import width="20px" />}
                        className={clsx(
                          'report-button d-flex',
                          !isDesktopViewport &&
                            'width-percent-100 justify-center',
                        )}
                      >
                        Download Log
                      </Button>
                    </Dropdown>
                  )}
                </div>
              </div>
            </Col>
          )}
          <Col span={isDesktopViewport ? 12 : 24}>
            {isEditControl && renderPropertiesDetails()}
          </Col>
          {includes(
            [REGISTER_STATUS_LABEL.DRAFT, REGISTER_STATUS_LABEL.UNPUBLISHED],
            registerData?.status,
          ) && !isDesktopViewport ? (
            <Col
              className="flex-vertical d-flex justify-end mt-10"
              span={!isDesktopViewport && 24}
            >
              <div className="d-flex justify-end form-buttons">
                <CanPerform action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}>
                  {!registerHasLogs && (
                    <Button
                      shape="round"
                      icon={<DeleteButton width={32} height={32} />}
                      className="width-percent-100 justify-center
                        delete-button-checklist delete-button d-flex"
                      onClick={() => {
                        setShowDeleteRegisterModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    shape="round"
                    type="primary"
                    icon={<SaveOutline />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'edit-button-checklist d-flex',
                    )}
                    disabled={
                      !(
                        propertyDetails?.length > 0 &&
                        tableHeaders?.length > 0 &&
                        registerName
                      )
                    }
                    onClick={() => handleSave()}
                  >
                    Go Live
                  </Button>
                </CanPerform>
              </div>
            </Col>
          ) : null}
          {isEditControl &&
            !isDesktopViewport &&
            includes(
              [REGISTER_STATUS_LABEL.LIVE, REGISTER_STATUS_LABEL.ARCHIVED],
              registerData?.status,
            ) && (
              <Col
                className="flex-vertical d-flex justify-end mt-10"
                span={!isDesktopViewport && 24}
              >
                <div className="d-flex justify-end">
                  <div className="form-buttons width-percent-100">
                    <CanPerform
                      action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
                    >
                      {registerData?.status === REGISTER_STATUS_LABEL.LIVE && (
                        <>
                          <Flex justify="end">{renderApprovers()}</Flex>
                          <Button
                            shape="round"
                            icon={<ArchiveOutline className="mr-5" />}
                            className={clsx(
                              'delete-button-checklist d-flex',
                              !isDesktopViewport &&
                                'width-percent-100 justify-center',
                            )}
                            onClick={() => handleRegisterArchive()}
                          >
                            Archive
                          </Button>
                          <Button
                            shape="round"
                            type="primary"
                            icon={<EditIcon />}
                            className={clsx(
                              'edit-button-checklist d-flex save-button',
                              !isDesktopViewport &&
                                'mt-10 width-percent-100 justify-center',
                            )}
                            onClick={() => {
                              setConfirmationType(CONFIRMATION_TYPES.EDIT);
                              setShowModal(true);
                            }}
                          >
                            Edit
                          </Button>
                        </>
                      )}
                      {registerData?.status ===
                        REGISTER_STATUS_LABEL.ARCHIVED && (
                        <Button
                          shape="round"
                          type="primary"
                          icon={<InboxOutlined />}
                          className={clsx(
                            !isDesktopViewport && 'justify-center fill-width',
                            'unarchive-button-checklist d-flex',
                          )}
                          onClick={() => {
                            setConfirmationType(CONFIRMATION_TYPES.UNARCHIVE);
                            setShowModal(true);
                          }}
                        >
                          Unarchive
                        </Button>
                      )}
                    </CanPerform>
                  </div>
                </div>
              </Col>
            )}
          <Col
            className="flex-vertical d-flex justify-end mt-10"
            span={!isDesktopViewport && 24}
          >
            {includes(
              [REGISTER_STATUS_LABEL.DRAFT, REGISTER_STATUS_LABEL.UNPUBLISHED],
              registerData?.status,
            ) &&
              !isDesktopViewport && (
                <CanPerform
                  action={ALLOWED_ACTION_KEYS.VIEW_USERS_PAGE}
                  type={ALLOWED_ACTION_TYPE.SYSTEM}
                >
                  <Flex>
                    {renderApprovers()}
                    <Button
                      shape="round"
                      icon={<UserAddOutlined />}
                      className={clsx(
                        'delete-button-checklist d-flex',
                        !isDesktopViewport &&
                          'width-percent-100 justify-center',
                      )}
                      onClick={handleAssignApprovers}
                    >
                      Assign Approvers
                    </Button>
                  </Flex>
                </CanPerform>
              )}
          </Col>
        </Row>
      </CommonCard>
      <RegisterFieldsTable
        registerId={registerId}
        ref={tableListRef}
        tableHeaders={tableHeaders}
        setTableHeaders={setTableHeaders}
        tableColumns={tableColumns}
        setTableColumns={setTableColumns}
        registerDetailsData={registerDetailsData}
        isEditControl={isEditControl}
        isEditable={isEditable}
        isDisabled={isDisabled}
        isProjectAdminOrInspector={isProjectAdminOrInspector}
        showDetailPrompt={showDetailPrompt}
        showTableFieldsPrompt={showTableFieldsPrompt}
        handleTableRouterPromptUpdate={handleTableRouterPromptUpdate}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        historyData={historyData}
        showAll={showAll}
        showDeletedEntries={showDeletedEntries}
        handleHisotryRefetch={handleHisotryRefetch}
        registerLog={registerLog}
        selectedAggregates={selectedAggregates}
        setSelectedAggregates={setSelectedAggregates}
        handleRegisterLogDetailRefetch={handleRegisterLogDetailRefetch}
        fieldsData={fieldsData}
        onTableErrorCheck={(hasErrors) => setTableFormHasErrors(hasErrors)}
        onTableDirty={() => setIsDirty(true)}
        handleSaveAsDraft={handleSaveAsDraft}
        onTableSave={(hasErrors) => {
          const isTableDirty = tableListRef?.current?.isTableDirty?.() ?? false;
          // Only consider dirty if refs indicate unsaved changes or table has pending modifications
          const hasUnsavedChanges = showDetailPrompt?.current || showTableFieldsPrompt?.current || isTableDirty;
          setIsDirty(hasUnsavedChanges);
          setTableFormHasErrors(hasErrors);
        }}
        showTableFieldConfirmModal={showTableFieldConfirmModal}
        setShowTableFieldConfirmModal={setShowTableFieldConfirmModal}
      />
      {isDesktopViewport && !isEditControl && (
        <Portal portalId="save-as-draft">
          <CanPerform
            action={ALLOWED_ACTION_KEYS?.ADD_REGISTER_LOG}
            type={ALLOWED_ACTION_TYPE?.PROJECT}
          >
            <Button
              type="primary"
              shape="round"
              className="save-button"
              onClick={() => handleSaveAsDraft(true)}
              loading={saveRegisterLogToDraftLoading}
              disabled={
                !isDirty || (!detailsFormHasErrors && !tableFormHasErrors)
              }
            >
              Save as Draft
            </Button>
          </CanPerform>
        </Portal>
      )}
      {showModal && (
        <ConfirmationModal
          type={confirmationType}
          showModal={showModal}
          setShowModal={setShowModal}
          handleRefetch={handleRegisterRefetch}
        />
      )}
      {showSaveModal && (
        <SaveRegisterLogModal
          handleRefetch={handleRegisterLogDetailRefetch}
          properties={properties}
          propertyDetails={propertyDetails}
          showModal={showSaveModal}
          setShowModal={setShowSaveModal}
          isEditControl={isEditControl}
          setIsEditControl={setIsEditControl}
          isEditableProperty={isEditableProperty}
          setIsEditableProperty={setIsEditableProperty}
        />
      )}
      {showDeleteRegisterModal && (
        <DeleteRegisterModal
          showModal={showDeleteRegisterModal}
          setShowModal={setShowDeleteRegisterModal}
          registerData={registerData}
        />
      )}
      {showApprovalModal && (
        <div className="register-approval-modal">
          <Modal
            maskClosable={false}
            title="Assign Approvers"
            open={showApprovalModal}
            onCancel={() => setShowApprovalModal(false)}
            footer={null}
            width={800}
            centered
            destroyOnHidden
            closeIcon={<WrongIcon />}
            getContainer={() =>
              // eslint-disable-next-line no-undef
              document.querySelector('.register-approval-modal')
            }
          >
            <RegisterApprovalModal
              isUpdate={registerData?.approvers?.length > 0}
              registerData={registerData}
              registerId={registerId}
              isEditable={
                isEditable ||
                registerData?.status === REGISTER_STATUS_LABEL.LIVE
              }
              refetchRef={refetchRef}
              affectedLogsCount={affectedLogsCount}
              onClose={() => setShowApprovalModal(false)}
            />
          </Modal>
        </div>
      )}
      {showSeekApprovalModal && (
        <SeekApprovalModal
          showModal={showSeekApprovalModal}
          setShowModal={setShowSeekApprovalModal}
          registerLogStatus={registerDetailsData?.status}
          registerData={registerDetailsData}
          registerLogId={registerLogId}
          onComplete={handleSeekApprovalComplete}
          registerLog={registerLog}
        />
      )}
      {open && !isDesktopViewport && (
        <ApproversDrawer open={open} setOpen={setOpen} height={350}>
          <div onClick={(e) => e.stopPropagation()}>
            <Descriptions
              column={3}
              layout="vertical"
              colon={false}
              className="inner"
            >
              {getLevelWiseApprovers(drawerData)}
            </Descriptions>
          </div>
        </ApproversDrawer>
      )}
      {showDetailsCommentModal && selectedDetailsComment && (
        <RegisterCommentModal
          showModal={showDetailsCommentModal}
          setShowModal={() => {
            setShowDetailsCommentModal(false);
            setSelectedDetailsComment(null);
          }}
          comment={selectedDetailsComment}
        />
      )}
      {!showTableFieldConfirmModal && (
        <UnsavedChangesPrompt
          openPrompt={isDirty}
          onSaveDraft={() => handleSaveAsDraft(false)}
        />
      )}
    </div>
  );
};

export default RegisterPropertyDetails;
